import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Typography, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';

export default function PWAInstallBanner() {
  const { language, isSectionVisible } = useLanguage();
  const { isDarkMode } = useTheme();

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  // Check if PWA install prompt is enabled by the admin dashboard
  const isPwaEnabled = isSectionVisible ? isSectionVisible('pwa') : true;

  useEffect(() => {
    // If disabled from admin dashboard, do not attach listeners and ensure prompt is hidden
    if (!isPwaEnabled) {
      setShowPrompt(false);
      setShowIOSGuide(false);
      return;
    }

    // Check if already running as standalone PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isStandalone) {
      return;
    }

    // Check if previously dismissed in this session
    if (sessionStorage.getItem('pwa_prompt_dismissed') === 'true') {
      return;
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
    setIsIOS(isIosDevice);

    // Standard Chromium / Edge / Android install prompt handler
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Wait a few seconds after initial page load for non-intrusive appearance
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 4000);
      return () => clearTimeout(timer);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      sessionStorage.setItem('pwa_prompt_dismissed', 'true');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isPwaEnabled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      if (isIOS) {
        setShowIOSGuide(true);
      }
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSGuide(false);
    sessionStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  // If disabled from dashboard or nothing to show, return null
  if (!isPwaEnabled || (!showPrompt && !showIOSGuide)) {
    return null;
  }

  // Multilingual text dictionary
  const texts = {
    en: {
      title: 'Install ITFORSEC App',
      desc: 'Fast, offline access to IT security & support right on your home screen or desktop.',
      installBtn: 'Install App',
      iosGuide: 'To install on iOS: tap the Share button and select "Add to Home Screen".',
      close: 'Dismiss'
    },
    ge: {
      title: 'დააინსტალირეთ ITFORSEC',
      desc: 'სწრაფი წვდომა IT უსაფრთხოებასა და სერვისებზე პირდაპირ თქვენს ეკრანზე.',
      installBtn: 'დაინსტალირება',
      iosGuide: 'iOS-ზე დასაყენებლად: დააჭირეთ Share ღილაკს და აირჩიეთ "Add to Home Screen".',
      close: 'დახურვა'
    },
    ru: {
      title: 'Установить приложение ITFORSEC',
      desc: 'Быстрый доступ к услугам кибербезопасности и IT прямо с рабочего стола.',
      installBtn: 'Установить',
      iosGuide: 'Для установки на iOS: нажмите кнопку «Поделиться» и выберите «На экран „Домой“».',
      close: 'Закрыть'
    }
  };

  const t = texts[language] || texts.en;

  return (
    <Slide direction="up" in={showPrompt || showIOSGuide} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 'auto' },
          right: { xs: 16, sm: 24 },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 420 },
          zIndex: 9999,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(11, 17, 32, 0.98))'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98))',
          backdropFilter: 'blur(16px)',
          border: isDarkMode
            ? '1px solid rgba(0, 200, 150, 0.35)'
            : '1px solid rgba(0, 200, 150, 0.45)',
          boxShadow: isDarkMode
            ? '0 12px 40px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 200, 150, 0.2)'
            : '0 12px 35px rgba(0, 0, 0, 0.12), 0 0 15px rgba(0, 200, 150, 0.15)',
          borderRadius: '14px',
          p: 2.2,
          color: isDarkMode ? '#fff' : '#0f172a',
          transition: 'background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(0, 200, 150, 0.2), rgba(46, 91, 255, 0.2))',
                border: '1px solid rgba(0, 200, 150, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00c896'
              }}
            >
              <ShieldOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '14px', letterSpacing: '0.3px', color: isDarkMode ? '#fff' : '#0f172a' }}>
                {t.title}
              </Typography>
              <Typography variant="caption" sx={{ color: '#00c896', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Progressive Web App
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              '&:hover': {
                color: isDarkMode ? '#fff' : '#0f172a',
                background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'
              }
            }}
            aria-label={t.close}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ color: isDarkMode ? '#cbd5e1' : '#475569', fontSize: '12.5px', lineHeight: 1.5, mb: 2 }}>
          {showIOSGuide ? t.iosGuide : t.desc}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={handleDismiss}
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              fontSize: '12px',
              textTransform: 'none',
              px: 1.5,
              '&:hover': {
                color: isDarkMode ? '#fff' : '#0f172a',
                background: 'transparent'
              }
            }}
          >
            {t.close}
          </Button>

          {showIOSGuide ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<IosShareIcon />}
              onClick={handleDismiss}
              sx={{
                background: 'linear-gradient(135deg, #00c896, #2e5bff)',
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'none',
                px: 2,
                borderRadius: '8px',
                boxShadow: '0 4px 14px rgba(0, 200, 150, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #00e0a7, #3b6cff)' }
              }}
            >
              OK, Got It
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<GetAppRoundedIcon />}
              onClick={handleInstallClick}
              sx={{
                background: 'linear-gradient(135deg, #00c896, #2e5bff)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'none',
                px: 2,
                borderRadius: '8px',
                boxShadow: '0 4px 14px rgba(0, 200, 150, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #00e0a7, #3b6cff)' }
              }}
            >
              {t.installBtn}
            </Button>
          )}
        </Box>
      </Box>
    </Slide>
  );
}
